import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import { Computer } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { io, Socket } from 'socket.io-client';
import * as api from '../../services/api';
import { API_URL, currentAppOrigin, SOCKET_URL } from '../../config/api';
import MachineDeviceMgmtTabs from '../shared/MachineDeviceMgmtTabs';
import MachineRenameControl from '../shared/MachineRenameControl';

const formatDateTime = (value: string) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const AdminDesktopMachineDetail: React.FC = () => {
  const { machineCode } = useParams<{ machineCode: string }>();
  const [machineDetail, setMachineDetail] = useState<api.MachineRandomCodeResponse['machine'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedTarget, setCopiedTarget] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!machineCode) return;

    const load = async () => {
      try {
        setLoading(true);
        const response = await api.getMachineRandomCode(machineCode);
        setMachineDetail(response.machine);
      } catch (error) {
        console.error('Error loading machine detail:', error);
        toast.error('Không tải được thông tin máy');
        setMachineDetail(null);
      } finally {
        setLoading(false);
      }
    };

    load();

    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 1500,
      path: '/socket.io',
    });
    socketRef.current = socket;
    const code = machineCode.toUpperCase();

    socket.on('connect', () => {
      socket.emit('join-machine-room', code);
    });

    socket.on('random-code-updated', (data: {
      machineCode: string;
      randomCode: string;
      fullCode: string;
      randomCodeUpdatedAt: string;
    }) => {
      if (!data?.machineCode || data.machineCode.toUpperCase() !== code) return;
      setMachineDetail((prev) =>
        prev
          ? {
              ...prev,
              randomCode: data.randomCode,
              fullCode: data.fullCode,
              randomCodeUpdatedAt: data.randomCodeUpdatedAt,
            }
          : prev
      );
    });

    socket.on('app-status-update', (data: {
      machineCode: string;
      isAppRunning: boolean;
      appPlatform?: 'windows' | 'android' | null;
      appProduct?: 'lite' | 'mono' | 'pro' | null;
    }) => {
      if (!data?.machineCode || data.machineCode.toUpperCase() !== code) return;
      setMachineDetail((prev) =>
        prev
          ? {
              ...prev,
              isAppRunning: data.isAppRunning,
              appPlatform: data.isAppRunning ? data.appPlatform ?? prev.appPlatform : prev.appPlatform,
              appProduct: data.isAppRunning ? data.appProduct ?? prev.appProduct : prev.appProduct,
            }
          : prev
      );
    });

    return () => {
      socket.emit('leave-machine-room', code);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [machineCode]);

  const handleCopy = async (text: string, target: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTarget(target);
      toast.success(target === 'random' ? 'Đã copy mã' : 'Đã copy link');
      setTimeout(() => setCopiedTarget(null), 2000);
    } catch {
      toast.error('Không copy được');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!machineDetail) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h6">Không tìm thấy máy</Typography>
      </Container>
    );
  }

  const uploadPageUrl = `${currentAppOrigin()}/mobile-upload/machine/${machineDetail.fullCode}`;
  const backendOrigin = API_URL.replace(/\/api\/?$/i, '');
  const controlPageUrl = `${backendOrigin}/controlpage/${machineDetail.machineCode}`;
  const editPageUrl = `${backendOrigin}/editpage/${machineDetail.machineCode}`;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 1.5, md: 3 }, px: { xs: 1.5, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, md: 2 },
          borderRadius: 3,
          border: '1px solid transparent',
          background:
            'linear-gradient(165deg, #ffffff 0%, #fff8fb 58%, #fff5f8 100%) padding-box, linear-gradient(135deg, rgba(255,139,167,0.95), rgba(244,143,177,0.55)) border-box',
          boxShadow: '0 14px 36px rgba(191, 95, 130, 0.08)',
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              background: 'linear-gradient(135deg, #ffc1d4 0%, #ff8ba7 52%, #f48fb1 100%)',
              boxShadow: '0 6px 14px rgba(191, 95, 130, 0.28)',
            }}
          >
            <Computer sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight={800} sx={{ fontSize: { xs: '0.95rem', md: '1.2rem' } }}>
            {machineDetail.machineName || machineDetail.machineCode}
          </Typography>
          <MachineRenameControl
            machineCode={machineDetail.machineCode}
            machineName={machineDetail.machineName}
            onRenamed={(machineName) =>
              setMachineDetail((prev) => (prev ? { ...prev, machineName } : prev))
            }
          />
          <Chip
            label={machineDetail.isAppRunning ? 'Đang hoạt động' : 'Tạm dừng'}
            color={machineDetail.isAppRunning ? 'success' : 'error'}
            size="small"
            sx={{ fontWeight: 700 }}
          />
        </Box>

        <MachineDeviceMgmtTabs
          userId={machineDetail.userId || undefined}
          machineDetail={machineDetail}
          uploadPageUrl={uploadPageUrl}
          controlPageUrl={controlPageUrl}
          editPageUrl={editPageUrl}
          copiedTarget={copiedTarget}
          onCopy={handleCopy}
          formatDateTime={formatDateTime}
          manageControlPageUi
        />
      </Paper>
    </Container>
  );
};

export default AdminDesktopMachineDetail;
